import { useState, type FormEvent } from "react";
import { AlertTriangle, Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "@/components/layout/PageHeader";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  useAppConfig,
  useBillingConfig,
  useSetAppConfig,
  useSetBillingConfig,
} from "@/hooks/use-config";
import { describeCallableError } from "@/lib/callables";
import { formatDateTime } from "@/lib/format";

/** Três segmentos numéricos, o mesmo formato que os aplicativos comparam. */
const VERSION_PATTERN = /^\d+\.\d+\.\d+$/;

export default function Settings() {
  const appConfig = useAppConfig();
  const billingConfig = useBillingConfig();
  const setApp = useSetAppConfig();
  const setBilling = useSetBillingConfig();

  // Cada campo mostra o que a pessoa digitou ou, enquanto ela não mexe, o
  // valor que está no ar. Sem isso o formulário nasceria vazio e salvar
  // apagaria o valor publicado.
  const [clientMinDraft, setClientMin] = useState<string | null>(null);
  const [sellerMinDraft, setSellerMin] = useState<string | null>(null);
  const [feeDraft, setFee] = useState<string | null>(null);
  const [confirmingFee, setConfirmingFee] = useState(false);

  const clientMin = clientMinDraft ?? appConfig.data?.clientMinVersion ?? "";
  const sellerMin = sellerMinDraft ?? appConfig.data?.sellerMinVersion ?? "";
  const fee =
    feeDraft ??
    (billingConfig.data ? String(billingConfig.data.platformFeePercentage ?? 10) : "");

  async function saveVersions(event: FormEvent) {
    event.preventDefault();
    const client = clientMin.trim();
    const seller = sellerMin.trim();

    if (client && !VERSION_PATTERN.test(client)) {
      toast.error("A versão do cliente precisa ter o formato 1.0.0.");
      return;
    }
    if (seller && !VERSION_PATTERN.test(seller)) {
      toast.error("A versão do vendedor precisa ter o formato 1.0.0.");
      return;
    }

    try {
      await setApp.mutateAsync({
        clientMinVersion: client || undefined,
        sellerMinVersion: seller || undefined,
      });
      toast.success("Versões mínimas atualizadas.");
    } catch (error) {
      toast.error(describeCallableError(error));
    }
  }

  async function saveFee() {
    const value = Number(fee);
    if (!Number.isFinite(value) || value < 0 || value > 100) {
      toast.error("A taxa precisa ser um número entre 0 e 100.");
      return;
    }
    try {
      await setBilling.mutateAsync({ platformFeePercentage: value });
      toast.success("Taxa da plataforma atualizada.");
      setConfirmingFee(false);
    } catch (error) {
      toast.error(describeCallableError(error));
    }
  }

  const currentFee = billingConfig.data?.platformFeePercentage ?? 10;
  const feeChanged = Number(fee) !== currentFee;

  return (
    <>
      <PageHeader
        title="Configurações"
        description="Os valores que o banco guarda e os aplicativos leem no arranque."
      />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Versões mínimas dos aplicativos</CardTitle>
            <CardDescription>
              Cada aplicativo compara a própria versão com estes valores ao abrir. Abaixo da mínima,
              ele mostra a tela de atualização. O bloqueio é dispensável pelo usuário, então isto é
              um pedido forte, e não uma trava.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {appConfig.isLoading ? (
              <div className="space-y-3">
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
              </div>
            ) : (
              <form onSubmit={saveVersions} className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="client-min">Aplicativo do cliente</Label>
                  <Input
                    id="client-min"
                    value={clientMin}
                    onChange={(e) => setClientMin(e.target.value)}
                    placeholder="1.0.20"
                    inputMode="decimal"
                    className="font-mono"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="seller-min">Aplicativo do vendedor</Label>
                  <Input
                    id="seller-min"
                    value={sellerMin}
                    onChange={(e) => setSellerMin(e.target.value)}
                    placeholder="1.0.17"
                    inputMode="decimal"
                    className="font-mono"
                  />
                </div>

                <div className="rounded-md border border-state-warning/40 bg-state-warning/10 p-3">
                  <p className="flex items-start gap-2 text-caption">
                    <AlertTriangle className="mt-0.5 size-4 shrink-0 text-state-warning" />
                    <span>
                      Uma versão acima da publicada nas lojas mostra a tela de atualização para a
                      base inteira, e a loja ainda não terá a versão nova para oferecer. Confira o
                      que está publicado antes de subir.
                    </span>
                  </p>
                </div>

                <Button type="submit" disabled={setApp.isPending}>
                  {setApp.isPending ? <Loader2 className="animate-spin" /> : <Save />}
                  Salvar versões
                </Button>
              </form>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Taxa da plataforma</CardTitle>
            <CardDescription>
              O percentual sobre o que cada vendedor entrega no mês. É lido pelo cálculo diário de
              faturamento, no backend.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {billingConfig.isLoading ? (
              <Skeleton className="h-10 w-full" />
            ) : (
              <div className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="fee">Percentual</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      id="fee"
                      type="number"
                      min={0}
                      max={100}
                      step="0.5"
                      value={fee}
                      onChange={(e) => setFee(e.target.value)}
                      className="max-w-32 font-mono"
                    />
                    <span className="text-medium text-muted-foreground">por cento</span>
                  </div>
                  {billingConfig.data?.updatedAt ? (
                    <p className="text-caption text-muted-foreground">
                      Última alteração em {formatDateTime(billingConfig.data.updatedAt)}.
                    </p>
                  ) : null}
                </div>

                <div className="rounded-md border border-state-warning/40 bg-state-warning/10 p-3">
                  <p className="flex items-start gap-2 text-caption">
                    <AlertTriangle className="mt-0.5 size-4 shrink-0 text-state-warning" />
                    <span>
                      A taxa vale a partir do próximo cálculo, às 03:00. As faturas já geradas
                      guardam o percentual que foi aplicado nelas e não mudam.
                    </span>
                  </p>
                </div>

                <Button
                  onClick={() => setConfirmingFee(true)}
                  disabled={!feeChanged || setBilling.isPending}
                >
                  <Save /> Alterar taxa
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <AlertDialog open={confirmingFee} onOpenChange={setConfirmingFee}>
        <AlertDialogContent>
          <AlertDialogTitle>
            Mudar a taxa de {currentFee}% para {fee}%?
          </AlertDialogTitle>
          <AlertDialogDescription>
            Isto muda quanto todos os vendedores pagam à plataforma, a partir do próximo cálculo
            diário. As faturas já geradas não mudam.
          </AlertDialogDescription>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => void saveFee()} disabled={setBilling.isPending}>
              {setBilling.isPending ? <Loader2 className="animate-spin" /> : null}
              Confirmar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
